import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { CustomSelect } from "@/components/ui/custom-select";
import { cn } from "@/lib/utils";
import { gameService } from "@/services/game.service";
import { Icon } from "@/components/ui/icon"

interface GameViplayConfigFieldsProps {
  arenaId: string;
  quadraIds: string[];
  viplayAutoSync: boolean;
  onArenaIdChange: (arenaId: string) => void;
  onQuadraIdsChange: (quadraIds: string[]) => void;
  onViplayAutoSyncChange: (enabled: boolean) => void;
  disabled?: boolean;
  /** Oculta opção de sync automático (ex.: modal manual) */
  showAutoSync?: boolean;
  /** Dentro de FormSection no cadastro — sem card/título duplicado */
  embedded?: boolean;
  className?: string;
}

export function GameViplayConfigFields({
  arenaId,
  quadraIds,
  viplayAutoSync,
  onArenaIdChange,
  onQuadraIdsChange,
  onViplayAutoSyncChange,
  disabled,
  showAutoSync = true,
  embedded = false,
  className,
}: GameViplayConfigFieldsProps) {
  const arenasQuery = useQuery({
    queryKey: ["viplayArenas"],
    queryFn: () => gameService.getViplayArenas(),
    staleTime: 1000 * 60 * 10,
    retry: 1,
  });

  const arenaOptions = useMemo(
    () =>
      (arenasQuery.data?.arenas ?? []).map((a) => ({
        value: a.objectId,
        label: a.nomeArena,
      })),
    [arenasQuery.data],
  );

  const selectedArena = useMemo(
    () => arenasQuery.data?.arenas.find((a) => a.objectId === arenaId),
    [arenasQuery.data, arenaId],
  );

  const toggleQuadra = (quadraObjectId: string) => {
    if (quadraIds.includes(quadraObjectId)) {
      onQuadraIdsChange(quadraIds.filter((id) => id !== quadraObjectId));
      return;
    }
    onQuadraIdsChange([...quadraIds, quadraObjectId]);
  };

  const selectAllQuadras = () => {
    onQuadraIdsChange([]);
  };

  const arenasErrorMessage =
    (arenasQuery.error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
    (arenasQuery.error instanceof Error ? arenasQuery.error.message : null);

  return (
    <div
      className={cn(
        embedded ? "flex flex-col gap-4" : "space-y-3 rounded-xl border border-border bg-muted/10 p-4",
        className)}
    >
      {!embedded && (
        <div className="flex items-center gap-2">
          <Icon name="videocam" />
          <div>
            <p className="text-sm font-bold text-foreground">Lances automáticos</p>
            <p className="text-xs text-muted-foreground">
              Arena e quadras para importar lances na galeria após o jogo.
            </p>
          </div>
        </div>
      )}

      {arenasQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando arenas...</p>
      ) : arenasQuery.isError ? (
        <p className="text-sm text-destructive">
          {arenasErrorMessage || "Não foi possível carregar as arenas."}
        </p>
      ) : (
        <>
          <CustomSelect
            label="Arena"
            value={arenaId}
            onChange={(value) => {
              onArenaIdChange(value);
              onQuadraIdsChange([]);
            }}
            options={arenaOptions}
            placeholder="Nenhuma (desativado)"
            disabled={disabled}
            helperText={
              arenaId
                ? "Selecione uma ou mais quadras abaixo. Vazio = todas."
                : `${arenaOptions.length} arena(s) disponíveis`
            }
          />

          {selectedArena && selectedArena.quadras.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold text-foreground ml-1">Quadras</span>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={selectAllQuadras}
                  className="text-xs font-semibold text-primary hover:underline disabled:opacity-50"
                >
                  Todas
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedArena.quadras.map((quadra) => {
                  const selected = quadraIds.length === 0 || quadraIds.includes(quadra.objectId);
                  const explicit = quadraIds.includes(quadra.objectId);
                  return (
                    <label
                      key={quadra.objectId}
                      className={cn(
                        "flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors",
                        selected
                          ? "border-primary/40 bg-primary/10 text-foreground"
                          : "border-border bg-card-surface text-muted-foreground",
                        disabled && "pointer-events-none opacity-50")}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        disabled={disabled}
                        onChange={() => {
                          if (quadraIds.length === 0) {
                            onQuadraIdsChange(
                              selectedArena.quadras
                                .map((q) => q.objectId)
                                .filter((id) => id !== quadra.objectId),
                            );
                            return;
                          }
                          toggleQuadra(quadra.objectId);
                        }}
                        className="h-4 w-4 rounded border-border accent-primary"
                      />
                      <span>{quadra.identificacao}</span>
                      {quadraIds.length === 0 && (
                        <span className="text-[10px] uppercase tracking-wide text-primary/80">incl.</span>
                      )}
                      {explicit && quadraIds.length > 0 && (
                        <Icon name="check" />
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {showAutoSync && (
            <>
              <div className="h-px bg-muted/30 dark:bg-white/10" />
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <label className="text-sm font-bold text-foreground">
                    Importação automática
                  </label>
                  <p className="text-xs text-muted-foreground">
                    15 minutos após o fim do jogo, os lances entram na galeria.
                  </p>
                </div>
                <label
                  className={cn(
                    "relative flex shrink-0 items-center cursor-pointer group",
                    (!arenaId || disabled) && "pointer-events-none opacity-50",
                  )}
                >
                  <input
                    type="checkbox"
                    className="peer h-6 w-6 cursor-pointer appearance-none rounded-lg border-2 border-border bg-muted/30 dark:bg-black/20 transition-all checked:border-primary checked:bg-primary"
                    checked={viplayAutoSync && !!arenaId}
                    disabled={disabled || !arenaId}
                    onChange={(e) => onViplayAutoSyncChange(e.target.checked)}
                  />
                  <Icon name="check" className={cn("absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 peer-checked:opacity-100 text-base pointer-events-none")} />
                </label>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
